import React, { useCallback, useEffect, useRef, useState } from 'react';
import apiService from './apiService';

/**
 * Mode maintenance (sites web) — enveloppe toute l'application.
 *  - consulte /app-status?app=web au chargement, au retour sur l'onglet et
 *    chaque minute (et immédiatement si une requête reçoit un 503 maintenance) ;
 *  - maintenance active (et pas de passe-droit testeur / activité en cours)
 *    → un écran plein, rien d'autre ;
 *  - maintenance planifiée à venir → bandeau d'annonce ;
 *  - serveur injoignable plusieurs fois de suite → écran « indisponible »
 *    avec « Réessayer » (cas d'un serveur réellement arrêté).
 */
const POLL_MS = 60000;
const FAILS_BEFORE_DOWN = 3;

const fmt = (iso) => (iso
  ? new Date(iso).toLocaleString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })
  : null);

export default function MaintenanceGate({ children }) {
  const [status, setStatus] = useState(null); // réponse /app-status
  const [down, setDown] = useState(false);
  const fails = useRef(0);

  const check = useCallback(async () => {
    try {
      const token = apiService.getToken('user') || apiService.getToken('driver');
      const res = await fetch(`${apiService.baseURL}/app-status?app=web`, {
        headers: { 'X-Mbiyo-App': 'web', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        cache: 'no-store',
      });
      if (!res.ok) throw new Error(String(res.status));
      setStatus(await res.json());
      fails.current = 0;
      setDown(false);
    } catch (_) {
      fails.current += 1;
      if (fails.current >= FAILS_BEFORE_DOWN) setDown(true);
    }
  }, []);

  useEffect(() => {
    check();
    const id = setInterval(check, POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    const onMaintenance = () => check();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('mbiyo:maintenance', onMaintenance);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('mbiyo:maintenance', onMaintenance);
    };
  }, [check]);

  if (status?.maintenance && !status.bypass) {
    return (
      <Screen
        emoji="🛠️"
        title="Mbiyo est en maintenance"
        text={status.message || "Nous améliorons Mbiyo pour vous. Le service revient très bientôt. Merci de votre patience !"}
        eta={status.eta ? `Retour prévu : ${fmt(status.eta)}` : null}
      />
    );
  }
  if (down) {
    return (
      <Screen
        emoji="📡"
        title="Service temporairement indisponible"
        text="Impossible de joindre Mbiyo pour le moment. Vérifiez votre connexion, ou réessayez dans quelques minutes."
        action={<button className="mb-maint-btn" onClick={check}>Réessayer</button>}
      />
    );
  }

  const ann = status?.announcement;
  return (
    <>
      {ann && (
        <div style={{
          position: 'sticky', top: 0, zIndex: 9999, background: '#1C1C1C', color: '#fff',
          padding: '9px 16px', textAlign: 'center', fontSize: '.88rem', fontWeight: 600,
        }}>
          🛠️ Maintenance prévue {fmt(ann.start)} — jusqu'à {new Date(ann.end).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}.
          Le service sera momentanément indisponible.
        </div>
      )}
      {children}
    </>
  );
}

function Screen({ emoji, title, text, eta, action }) {
  return (
    <div style={{
      minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24, background: 'linear-gradient(160deg, #1C1C1C 0%, #2A2E35 70%, #3A2018 100%)',
      color: '#fff', textAlign: 'center', fontFamily: 'var(--mb-font, system-ui)',
    }}>
      <div style={{ maxWidth: 460 }}>
        <img src="/logo192.png" alt="Mbiyo" style={{ width: 72, height: 72, borderRadius: 18, marginBottom: 22 }} />
        <div style={{ fontSize: 44, marginBottom: 6 }}>{emoji}</div>
        <h1 style={{ fontSize: '1.7rem', margin: '0 0 12px', fontWeight: 800 }}>{title}</h1>
        <p style={{ fontSize: '1rem', lineHeight: 1.6, color: 'rgba(255,255,255,.8)', margin: 0 }}>{text}</p>
        {eta && <p style={{ marginTop: 18, fontWeight: 700, color: '#FFB900' }}>{eta}</p>}
        {action && <div style={{ marginTop: 22 }}>{action}</div>}
      </div>
      <style>{`.mb-maint-btn{background:#F54119;color:#fff;border:none;border-radius:12px;padding:12px 26px;font-weight:800;font-size:1rem;cursor:pointer}`}</style>
    </div>
  );
}
